import { useCallback } from 'react';

import { useLocation, useNavigate } from '@suntex.core/nav';
import { $EntityLevel, EntityLevel } from '@suntex.fim/enums';

import { entityPath } from './utils/paths';

/** Navigate the current entity-scoped route to a sibling entity of the given level. */
export type SwitchEntity = (level: EntityLevel, id: number) => void;

/**
 * Build the callback that switches to a sibling entity. Same-level switches
 * preserve the current sub-route; cross-level switches (and player switches,
 * which are id-less) land on the dashboard.
 */
export function useSwitchEntity(): SwitchEntity {
  const location = useLocation();
  const navigate = useNavigate();
  return useCallback(
    (level, id) => {
      const segments = location.pathname.split('/');
      const currentSlug = segments[1] ?? '';
      const currentId = segments[2] ?? '';
      const targetSlug = $EntityLevel.describe(level).slug;
      // Cross-level switches and player switches go to the dashboard — sub-routes
      // aren't portable, and player routes carry no id.
      if (
        (currentSlug && targetSlug !== currentSlug) ||
        level === EntityLevel.Player
      ) {
        navigate(entityPath(level, id, 'dashboard'));
        return;
      }
      const newPath = currentId
        ? location.pathname.replace(`/${currentId}`, `/${id}`)
        : location.pathname;
      navigate(newPath);
    },
    [location.pathname, navigate],
  );
}
